import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { useLazyQuery, useMutation } from "@apollo/client";

import { PageHeader, Card, Button } from "@cofano/cck";
import { Grid, Box, Tabs, Tab } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import useTabs from "components/hooks/useTabs";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "imports/Utils";

import CargoDetails from "./components/createEdit/CargoDetails";
import Actions from "./components/createEdit/Actions";
import CargoRelations from "./components/createEdit/CargoRelations";
import OutOfGauge from "./components/createEdit/OutOfGauge";
import Deepsea from "./components/createEdit/Deepsea";
import VoyageSchedule from "./components/createEdit/VoyageSchedule";

import { CreateEditEntityInDialogProps } from "types/commons";

import { CargoTabs, getCargoFormDefaultValues, getCargoFormMutationInput } from "./utils/CargoUtils";
import { cargoForm_query } from "./utils/CargoQueries";
import { CargoActionForm, CargoForm, CargoLinkedCharter } from "types/index";
import { validateCargoActions } from "view/cargos/utils/CargoActionUtils";
import { SaveCargoFormDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { DateUtils } from "utils/DateUtils";
import moment from "moment";

const CargoCreateEdit = ({
	linkedCharter,
	pageInDialogSettings,
	validFrom,
	validUntil,
}: {
	linkedCharter?: CargoLinkedCharter;
	pageInDialogSettings?: CreateEditEntityInDialogProps;
	validFrom?: number | null;
	validUntil?: number | null;
}) => {

	const params = useParams();
	const id = pageInDialogSettings ? pageInDialogSettings.entityId : params.id;

	const navigate = useNavigate();
	const session = useStoreState(state => state.session);

	const useSimulatedTimestamps = hasPluginPermission(session, "cargoActionUseSimulatedTimestamps");

	const [ calculateWeightAndVolume, setCalculateWeightAndVolume ] = useState(true);

	const { isAdmin, isCompanyUser, isOperator, isTerminal, isMainUserLimited, isCustomer } = usePermission();

	const { selectedTab, selectTab } = useTabs<CargoTabs>({
		defaultSelected: CargoTabs.Actions,
		param: "cargotab",
		disableParams: !!pageInDialogSettings,
	});

	const [ getCargo, { loading: getCargoLoading } ] = useLazyQuery(cargoForm_query);
	const [ saveCargo, { loading: saveCargoLoading } ] = useMutation(SaveCargoFormDocument);

	const loading = getCargoLoading || saveCargoLoading;

	const { control, handleSubmit, reset, formState, setValue, watch, trigger, getValues, setError, clearErrors } = useForm<CargoForm>({
		defaultValues: getCargoFormDefaultValues(session, linkedCharter),
		mode: "onChange",
	});

	const actions = useWatch({ control: control, name: "actions" }) ?? [];

	const fieldEnabled = (field: string) => {
		return (
			(isAdmin || isCompanyUser || isOperator || isTerminal || isMainUserLimited || isCustomer) &&
			hasOptionalFieldPermission(session, Fields.ORDER, field)
		);
	};

	const beforeSave = async (data: CargoForm) => {
		const isFormValid = await trigger();

		if (!isFormValid) {
			return;
		}

		const actionsValid = onValidateCargoActions();
		if (!actionsValid) {
			return;
		}

		save(data);
	};

	const onValidateCargoActions = () => {
		return validateCargoActions(actions, setError, clearErrors, useSimulatedTimestamps, getValues);
	};

	const save = (data: CargoForm) => {
		saveCargo({
			variables: {
				input: getCargoFormMutationInput(data, useSimulatedTimestamps),
			},
		}).then((response) => {
			if (pageInDialogSettings?.onSuccess) {
				pageInDialogSettings.onSuccess(response.data?.saveCargo);
			} else {
				navigate(`/view/cargos/${response.data?.saveCargo?.id}/details`);
			}
		});
	};

	useEffect(() => {
		if (pageInDialogSettings?.onLoadingChange) {
			pageInDialogSettings.onLoadingChange(loading);
		}
	}, [ loading ]);

	useEffect(() => {
		if (id) {
			getCargo({
				variables: {
					id: Number(id),
				},
			}).then(response => {
				reset({
					...response.data?.cargo,
					actions: response.data?.cargo?.actions.map(action => ({
						...action,
						date: !action.displayAsWindow && !!action.start ? moment(action.start).toDate().toDateString() : undefined,
						windowFrom: action.displayAsWindow ? DateUtils.getTimestamp(action.start) : undefined,
						windowTo: action.displayAsWindow ? DateUtils.getTimestamp(action.end) : undefined,
					} as CargoActionForm)),
				} as CargoForm);
			});
		}
	}, []);

	return (
		<div className="cargo-create-edit-page">
			<form id="cargo-form" onSubmit={(event) => void handleSubmit(beforeSave)(event)}>
				{!pageInDialogSettings && (
					<PageHeader>
						<PageHeader.Title>
							{Language("CARGO")}
							<Helmet title={intl.formatMessage({ id: "CARGO" })} />
						</PageHeader.Title>

						<PageHeader.Actions>
							<Button
								onClick={() => navigate(id ? `/view/cargos/${id}/details` : "/view/cargos")}
								disabled={loading}
								variant="outlined">
								{Language("CANCEL")}
							</Button>

							<Button
								disabled={loading}
								loading={loading}
								type="submit">
								{Language("SAVE")}
							</Button>
						</PageHeader.Actions>
					</PageHeader>
				)}

				<Grid spacing={3} container>
					<Grid md={12} lg={5} item>
						<Card>
							<CargoDetails
								calculateWeightAndVolume={calculateWeightAndVolume}
								setCalculateWeightAndVolume={setCalculateWeightAndVolume}
								control={control}
								setValue={setValue}
								formState={formState}
								watch={watch}
							/>
						</Card>
					</Grid>

					<Grid md={12}
						lg={7}
						item>
						<Card disablePadding>
							<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
								<Tabs
									variant='scrollable'
									value={selectedTab}
									onChange={(event, newValue) => selectTab(newValue)}>
									<Tab label={Language("CARGO_ACTIONS")} value={CargoTabs.Actions} />

									<Tab label={Language("RELATIONS")} value={CargoTabs.Relations} />

									{fieldEnabled("outOfGauge") && (
										<Tab label={Language("CARGO_OOG")} value={CargoTabs.Oog} />
									)}

									{fieldEnabled("deepsea") && (
										<Tab label={Language("CARGO_DEEPSEA_CONNECTION")} value={CargoTabs.Deepsea} />
									)}

									{fieldEnabled("voyageInfo") && (
										<Tab label={Language("VOYAGE_SCHEDULE")} value={CargoTabs.VoyageSchedule} />
									)}
								</Tabs>
							</Box>

							{selectedTab === CargoTabs.Actions && (
								<div style={{ backgroundColor: "#F0F0F0", padding: 16 }}>
									<Actions
										calculateWeightAndVolume={calculateWeightAndVolume}
										control={control}
										formState={formState}
										setValue={setValue}
										validFrom={validFrom}
										validUntil={validUntil}
										trigger={trigger}
										getValues={getValues}
										validateCargoActions={onValidateCargoActions}
									/>
								</div>
							)}

							{selectedTab === CargoTabs.Relations && (
								<div style={{ padding: 16 }}>
									<CargoRelations control={control} session={session} />
								</div>
							)}


							{selectedTab === CargoTabs.Oog && fieldEnabled("outOfGauge") && (
								<div style={{ padding: 16 }}>
									<OutOfGauge control={control} />
								</div>
							)}

							{selectedTab === CargoTabs.Deepsea && fieldEnabled("deepsea") && (
								<div style={{ padding: 16 }}>
									<Deepsea control={control} />
								</div>
							)}

							{selectedTab === CargoTabs.VoyageSchedule && fieldEnabled("voyageInfo") && (
								<div style={{ padding: 16 }}>
									<VoyageSchedule control={control} />
								</div>
							)}
						</Card>
					</Grid>
				</Grid>
			</form>
		</div>
	);
};

export default CargoCreateEdit;
